<template>
  <div class="erf-overlay" @click.self="closeForm">
    <div class="erf-modal" role="dialog" aria-modal="true" aria-labelledby="erf-title">
      <!-- Header -->
      <header class="erf-header">
        <div class="erf-header__title-wrap">
          <p class="erf-eyebrow">Edit MLS rental history</p>
          <h2 id="erf-title" class="erf-title">
            Entry
            <span class="erf-entry-tag">
              <span class="erf-entry-tag__hash">#</span>{{ rentalData.entry || "—" }}
            </span>
            <span class="erf-unit" v-if="mlsUnit">
              · Unit <b>{{ mlsUnit }}</b>
            </span>
          </h2>
        </div>
        <button
          type="button"
          class="erf-close"
          aria-label="Close"
          @click="closeForm"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
          </svg>
        </button>
      </header>

      <form id="edit-rental-form" class="erf-body" @submit.prevent="onSubmit">
        <!-- Listing -->
        <section class="erf-section">
          <div class="erf-section__head">
            <h3>Listing</h3>
            <span class="erf-section__rule"></span>
          </div>
          <div class="erf-grid">
            <div class="erf-field">
              <label class="erf-label" for="erf-list-price">
                List price <span class="erf-req">*</span>
              </label>
              <div class="erf-ctrl">
                <span class="erf-ctrl__adorn">$</span>
                <input
                  id="erf-list-price"
                  v-model="rentalData.list_price"
                  type="text"
                  inputmode="decimal"
                  placeholder="0.00"
                />
                <span class="erf-ctrl__icon" aria-hidden="true">/mo</span>
              </div>
            </div>
            <div class="erf-field">
              <label class="erf-label" for="erf-price-date">Price date</label>
              <div class="erf-ctrl">
                <input
                  id="erf-price-date"
                  v-model="rentalData.price_date"
                  type="date"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Outcome -->
        <section class="erf-section">
          <div class="erf-section__head">
            <h3>Outcome</h3>
            <span class="erf-section__rule"></span>
          </div>
          <div class="erf-grid">
            <div class="erf-field">
              <label class="erf-label" for="erf-sold-date">Leased date</label>
              <div class="erf-ctrl">
                <input
                  id="erf-sold-date"
                  v-model="rentalData.sold_date"
                  type="date"
                />
              </div>
            </div>
            <div class="erf-field">
              <label class="erf-label" for="erf-sold-price">Leased price</label>
              <div class="erf-ctrl">
                <span class="erf-ctrl__adorn">$</span>
                <input
                  id="erf-sold-price"
                  v-model="rentalData.sold_price"
                  type="text"
                  inputmode="decimal"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Documentation -->
        <section class="erf-section">
          <div class="erf-section__head">
            <h3>Documentation</h3>
            <span class="erf-section__rule"></span>
          </div>
          <div class="erf-grid">
            <div class="erf-field">
              <label class="erf-label" for="erf-backup-file">Back-up file</label>
              <label
                class="erf-file"
                :class="{ 'erf-file--has': !!backup_file, 'is-dragover': dragOver }"
                for="erf-backup-file"
                @dragenter.prevent="dragOver = true"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="onDrop"
              >
                <span class="erf-file__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </span>
                <span class="erf-file__text">
                  <span class="erf-file__primary">
                    <template v-if="backup_file">{{ backup_file.name }}</template>
                    <template v-else>Drop a file or browse</template>
                  </span>
                  <span class="erf-file__hint">
                    <template v-if="backup_file">
                      {{ formatFileSize(backup_file.size) }} · attached
                    </template>
                    <template v-else>PDF, PNG, JPG · up to 10 MB</template>
                  </span>
                </span>
                <span class="erf-file__action">
                  {{ backup_file ? "Replace" : "Browse" }}
                </span>
                <input
                  id="erf-backup-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  @change="onFileChange"
                />
              </label>
            </div>
            <div class="erf-field">
              <label class="erf-label" for="erf-backup-text">Backup text</label>
              <div class="erf-ctrl">
                <input
                  id="erf-backup-text"
                  v-model="rentalData.backupText"
                  type="text"
                  placeholder="MLS ref, agent notes…"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Status -->
        <section class="erf-section">
          <div class="erf-section__head">
            <h3>Status</h3>
            <span class="erf-section__rule"></span>
          </div>
          <div class="erf-grid erf-grid--full">
            <div class="erf-field">
              <label class="erf-label" for="erf-status">Listing status</label>
              <div class="erf-ctrl erf-select-wrap">
                <select
                  id="erf-status"
                  v-model="rentalData.status"
                  required
                >
                  <option :value="null" disabled>Select a status…</option>
                  <option
                    v-for="opt in statusOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.text || "—" }}
                  </option>
                </select>
                <span class="erf-chev" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m6 9 6 6 6-6"/>
                  </svg>
                </span>
              </div>
              <div class="erf-chips" role="group" aria-label="Quick set status">
                <button
                  v-for="opt in statusChips"
                  :key="opt.value"
                  type="button"
                  class="erf-chip"
                  :class="{ 'erf-chip--on': rentalData.status == opt.value }"
                  @click="rentalData.status = opt.value"
                >
                  <span class="erf-chip__dot" :style="{ background: opt.color }"></span>
                  {{ opt.text }}
                </button>
              </div>
            </div>
          </div>
        </section>
      </form>

      <footer class="erf-footer">
        <div class="erf-footer__left">
          <span class="erf-foot-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            Changes save to this rental entry.
          </span>
        </div>
        <div class="erf-footer__right">
          <button
            type="button"
            class="erf-btn erf-btn--ghost"
            @click="closeForm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="edit-rental-form"
            class="erf-btn erf-btn--primary"
            :disabled="submitting"
          >
            {{ submitting ? "Saving…" : "Save changes" }}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { BACKEND_URL } from "../../variables";

export default {
  name: "EditRentalForm",
  props: {
    mlsCid: { default: null },
    mlsUnit: { default: null },
    rentalData: { type: Object, required: true },
  },
  emits: ["close", "saved"],
  data() {
    return {
      backup_file: null,
      submitting: false,
      dragOver: false,
      statusOptions: [
        { text: "—", value: 11482 },
        { text: "Active", value: 8197 },
        { text: "Leased", value: 8198 },
      ],
      statusChips: [
        { text: "Active", value: 8197, color: "#1F9D55" },
        { text: "Leased", value: 8198, color: "#F26A6F" },
        { text: "—", value: 11482, color: "#8A8A8A" },
      ],
    };
  },
  mounted() {
    const insideBModal = this.$el && this.$el.closest && this.$el.closest(".modal");
    if (this.$el && this.$el.nodeType === 1 && !insideBModal) {
      document.body.appendChild(this.$el);
    }
    document.addEventListener("keydown", this.onKey);
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
    document.removeEventListener("keydown", this.onKey);
  },
  methods: {
    closeForm() {
      this.$emit("close");
      let parent = this.$parent;
      while (parent) {
        if (parent.$options && parent.$options.name === "BModal" && typeof parent.hide === "function") {
          parent.hide();
          break;
        }
        parent = parent.$parent;
      }
    },
    onKey(e) {
      if (e.key === "Escape") this.closeForm();
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      this.backup_file = file || null;
    },
    onDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.backup_file = file;
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return "";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
      return (bytes / 1024 / 1024).toFixed(1) + " MB";
    },
    isRealValue(obj) {
      return ![null, "-", "", 0, undefined].includes(obj);
    },
    async onSubmit() {
      this.submitting = true;
      const formData = new FormData();
      formData.append("rid", this.rentalData.rid);
      formData.append("entry", this.rentalData.entry);
      formData.append("status", this.rentalData.status);
      formData.append("soldDate", this.isRealValue(this.rentalData.sold_date) ? this.rentalData.sold_date : "");
      formData.append("priceDate", this.isRealValue(this.rentalData.price_date) ? this.rentalData.price_date : "");
      formData.append("listPrice", this.rentalData.list_price);
      formData.append("soldPrice", this.rentalData.sold_price);
      formData.append("backupfile", this.backup_file);
      formData.append("backupText", this.rentalData.backupText || "");

      try {
        await $.ajax({
          type: "POST",
          url: BACKEND_URL + "api/presale_mls_rental_entry_edit",
          data: formData,
          processData: false,
          contentType: false,
        }).done(() => {
          if (this.$bvToast) {
            this.$bvToast.toast("Saved successfully!", {
              title: "Great job!",
              variant: "success",
              autoHideDelay: 4000,
            });
          }
          this.$emit("saved");
          setTimeout(() => window.location.reload(), 1500);
        });
      } catch (error) {
        if (this.$bvToast) {
          this.$bvToast.toast("Save failed. Please try again later.", {
            title: "Ooops!",
            autoHideDelay: 5000,
            variant: "warning",
          });
        }
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ============================================================
   Edit Rental Form — RPD design system
   ============================================================ */
.erf-overlay {
  position: fixed;
  inset: 0;
  background: rgba(18, 40, 63, 0.55);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
  z-index: 1100;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
  font-family: var(--rpd-font-sans, "Inter Tight", "Inter", sans-serif);
}

.erf-modal {
  position: relative;
  width: min(680px, 100%);
  background: var(--rpd-white, #fff);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-lg, 16px);
  box-shadow: var(--rpd-shadow-lg, 0 24px 60px -20px rgba(14, 27, 44, 0.28));
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 80px);
  animation: erf-rise 280ms cubic-bezier(0.3, 0, 0, 1);
  color: var(--rpd-ink, #0a1320);
}
@keyframes erf-rise {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ---------- Header ---------- */
.erf-header {
  background: var(--rpd-navy-800, #15273d);
  color: #fff;
  padding: 22px 28px 22px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  position: relative;
  flex-shrink: 0;
}
.erf-header::after {
  content: "";
  position: absolute;
  left: 28px;
  bottom: 14px;
  width: 30px;
  height: 3px;
  background: var(--rpd-coral, #f26a6f);
  border-radius: 2px;
}
.erf-header__title-wrap { min-width: 0; flex: 1; }

.erf-eyebrow {
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  margin: 0 0 8px;
}
.erf-title {
  font-family: var(--rpd-font-display, "Fraunces", serif);
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: -0.01em;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  color: #fff;
  line-height: 1.25;
}
.erf-entry-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: var(--rpd-font-mono, "JetBrains Mono", monospace);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  background: rgba(242, 106, 111, 0.18);
  color: var(--rpd-coral, #f26a6f);
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid rgba(242, 106, 111, 0.35);
}
.erf-entry-tag__hash { opacity: 0.8; }
.erf-unit {
  font-family: var(--rpd-font-sans, "Inter Tight", sans-serif);
  font-weight: 400;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.7);
}
.erf-unit b { color: #fff; font-weight: 600; }

.erf-close {
  appearance: none;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: var(--rpd-r-sm, 6px);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
  flex-shrink: 0;
}
.erf-close:hover { background: rgba(255, 255, 255, 0.16); }
.erf-close svg { width: 16px; height: 16px; }

/* ---------- Body ---------- */
.erf-body {
  padding: 22px 28px 8px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1 1 auto;
  overflow-y: auto;
}

.erf-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.erf-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.erf-section__head h3 {
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--rpd-navy-800, #15273d);
  margin: 0;
}
.erf-section__rule {
  flex: 1;
  height: 1px;
  background: var(--rpd-line-2, #eff2f6);
}

.erf-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
}
.erf-grid--full { grid-template-columns: 1fr; }

.erf-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.erf-label {
  font-weight: 500;
  font-size: 0.8125rem;
  color: var(--rpd-ink, #0a1320);
  letter-spacing: 0.01em;
  margin: 0;
}
.erf-req { color: var(--rpd-coral, #f26a6f); margin-left: 2px; }

/* ---------- Control wrapper ---------- */
.erf-ctrl {
  position: relative;
  display: flex;
  align-items: stretch;
  background: var(--rpd-white, #fff);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-sm, 6px);
  transition: border-color 120ms ease, box-shadow 120ms ease;
  min-height: 40px;
}
.erf-ctrl:hover { border-color: var(--rpd-navy-400, #5a7795); }
.erf-ctrl:focus-within {
  border-color: var(--rpd-navy-800, #15273d);
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.18);
}
.erf-ctrl__adorn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px 0 12px;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--rpd-muted, #5c6b80);
  border-right: 1px solid var(--rpd-line-2, #eff2f6);
  background: var(--rpd-bg-2, #f4f6f9);
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}
.erf-ctrl input,
.erf-ctrl select {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0 12px;
  font-family: inherit;
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--rpd-ink, #0a1320);
  outline: none;
  height: 38px;
  appearance: none;
  -webkit-appearance: none;
}
.erf-ctrl input::placeholder { color: #9aa3b1; }
.erf-ctrl input[type="date"] { font-variant-numeric: tabular-nums; }
.erf-ctrl__icon {
  display: inline-flex;
  align-items: center;
  padding-right: 12px;
  color: var(--rpd-muted, #5c6b80);
  font-size: 0.75rem;
  pointer-events: none;
}

.erf-select-wrap select { padding-right: 36px; cursor: pointer; }
.erf-chev {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--rpd-muted, #5c6b80);
  pointer-events: none;
  display: inline-flex;
}
.erf-chev svg { width: 16px; height: 16px; stroke-width: 1.75; }

/* ---------- File input ---------- */
.erf-file {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px 9px 12px;
  border: 1px dashed var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-sm, 6px);
  background: var(--rpd-bg-2, #f4f6f9);
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
  margin: 0;
  min-height: 40px;
}
.erf-file:hover {
  border-color: var(--rpd-navy-700, #1e3753);
  background: var(--rpd-bg, #fafbfc);
}
.erf-file.is-dragover {
  border-color: var(--rpd-coral, #f26a6f);
  background: #fff4f4;
}
.erf-file--has {
  border-style: solid;
  border-color: var(--rpd-navy-700, #1e3753);
  background: var(--rpd-white, #fff);
}
.erf-file input[type="file"] {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.erf-file__icon {
  width: 28px;
  height: 28px;
  border-radius: var(--rpd-r-sm, 6px);
  background: var(--rpd-white, #fff);
  color: var(--rpd-navy-800, #15273d);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  border: 1px solid var(--rpd-line, #e3e8ef);
}
.erf-file__icon svg { width: 14px; height: 14px; stroke-width: 1.75; }
.erf-file__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.erf-file__primary {
  font-size: 0.8125rem;
  color: var(--rpd-ink, #0a1320);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.erf-file__hint {
  font-size: 0.6875rem;
  color: var(--rpd-muted, #5c6b80);
}
.erf-file__action {
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--rpd-navy-800, #15273d);
  padding: 5px 10px;
  border-radius: var(--rpd-r-sm, 6px);
  background: var(--rpd-white, #fff);
  border: 1px solid var(--rpd-line, #e3e8ef);
  flex: none;
}

/* ---------- Status chips ---------- */
.erf-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}
.erf-chip {
  appearance: none;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--rpd-bg-2, #f4f6f9);
  color: var(--rpd-muted, #5c6b80);
  border: 1px solid var(--rpd-line, #e3e8ef);
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.erf-chip:hover {
  border-color: var(--rpd-navy-700, #1e3753);
  color: var(--rpd-ink, #0a1320);
}
.erf-chip--on {
  background: var(--rpd-navy-800, #15273d);
  color: #fff;
  border-color: var(--rpd-navy-800, #15273d);
}
.erf-chip__dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  flex-shrink: 0;
}

/* ---------- Footer ---------- */
.erf-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  border-top: 1px solid var(--rpd-line-2, #eff2f6);
  background: var(--rpd-bg, #fafbfc);
  flex-shrink: 0;
  gap: 12px;
  flex-wrap: wrap;
}
.erf-footer__left,
.erf-footer__right { display: flex; align-items: center; gap: 10px; }
.erf-foot-note {
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.erf-foot-note svg { width: 14px; height: 14px; stroke-width: 1.75; }

.erf-btn {
  appearance: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 9px 16px;
  border-radius: var(--rpd-r-sm, 6px);
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
}
.erf-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.32);
}
.erf-btn[disabled] { opacity: 0.65; cursor: not-allowed; }
.erf-btn svg { width: 14px; height: 14px; }

.erf-btn--ghost {
  background: transparent;
  color: var(--rpd-ink, #0a1320);
  border-color: var(--rpd-line, #e3e8ef);
}
.erf-btn--ghost:hover {
  background: var(--rpd-white, #fff);
  border-color: var(--rpd-navy-700, #1e3753);
}
.erf-btn--primary {
  background: var(--rpd-navy-800, #15273d);
  color: #fff;
  border-color: var(--rpd-navy-800, #15273d);
}
.erf-btn--primary:hover {
  background: var(--rpd-navy-700, #1e3753);
  border-color: var(--rpd-navy-700, #1e3753);
}
.erf-btn--primary:active {
  background: var(--rpd-navy-900, #0e1b2c);
}

/* ---------- Responsive ---------- */
@media (max-width: 560px) {
  .erf-overlay { padding: 16px 12px; }
  .erf-modal { max-height: calc(100vh - 32px); }
  .erf-grid { grid-template-columns: 1fr; }
  .erf-header,
  .erf-body,
  .erf-footer { padding-left: 18px; padding-right: 18px; }
  .erf-header::after { left: 18px; }
  .erf-footer__right { width: 100%; }
  .erf-footer__right .erf-btn { flex: 1; justify-content: center; }
}

/* ============================================================
   Dark mode
   ============================================================ */
html.dark .erf-overlay { background: rgba(5, 10, 18, 0.7); }
html.dark .erf-modal {
  background: #0f1d30;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
  color: #e6ecf3;
}
html.dark .erf-header { background: #050e1c; }
html.dark .erf-section__head h3 { color: #c8d3e0; }
html.dark .erf-section__rule { background: rgba(255, 255, 255, 0.08); }
html.dark .erf-label { color: #e6ecf3; }
html.dark .erf-ctrl {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
}
html.dark .erf-ctrl:hover { border-color: rgba(255, 255, 255, 0.22); }
html.dark .erf-ctrl:focus-within {
  border-color: #fff;
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.35);
}
html.dark .erf-ctrl__adorn {
  background: rgba(255, 255, 255, 0.06);
  color: #b6c0cf;
  border-right-color: rgba(255, 255, 255, 0.08);
}
html.dark .erf-ctrl input,
html.dark .erf-ctrl select { color: #f0f4f9; }
html.dark .erf-ctrl input::placeholder { color: #7a8595; }
html.dark .erf-ctrl input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.85); }
html.dark .erf-ctrl__icon,
html.dark .erf-chev { color: #8a96a8; }

html.dark .erf-file {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.12);
}
html.dark .erf-file:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.22);
}
html.dark .erf-file--has {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.20);
}
html.dark .erf-file__icon {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.10);
  color: #fff;
}
html.dark .erf-file__primary { color: #f0f4f9; }
html.dark .erf-file__hint { color: #8a96a8; }
html.dark .erf-file__action {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
  color: #e6ecf3;
}

html.dark .erf-chip {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
  color: #b6c0cf;
}
html.dark .erf-chip:hover {
  border-color: rgba(255, 255, 255, 0.22);
  color: #f0f4f9;
}
html.dark .erf-chip--on {
  background: #fff;
  color: var(--rpd-navy-900, #0e1b2c);
  border-color: #fff;
}

html.dark .erf-footer {
  background: rgba(255, 255, 255, 0.02);
  border-top-color: rgba(255, 255, 255, 0.06);
}
html.dark .erf-foot-note { color: #8a96a8; }
html.dark .erf-btn--ghost {
  color: #e6ecf3;
  border-color: rgba(255, 255, 255, 0.12);
}
html.dark .erf-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.22);
}
html.dark .erf-btn--primary {
  background: #fff;
  color: var(--rpd-navy-900, #0e1b2c);
  border-color: #fff;
}
html.dark .erf-btn--primary:hover {
  background: #e6ecf3;
  border-color: #e6ecf3;
}
</style>
